import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Plus, Trash2, Cake, Calendar, Heart, Bell, Gift } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DATE_TYPES = [
  { id: 'birthday', label: 'Födelsedag', icon: Cake, color: 'bg-pink-100 text-pink-600' },
  { id: 'anniversary', label: 'Årsdag', icon: Heart, color: 'bg-red-100 text-red-600' },
  { id: 'appointment', label: 'Möte/Besök', icon: Calendar, color: 'bg-blue-100 text-blue-600' },
  { id: 'reminder', label: 'Påminnelse', icon: Bell, color: 'bg-amber-100 text-amber-600' },
  { id: 'other', label: 'Annat', icon: Gift, color: 'bg-purple-100 text-purple-600' },
];

export default function ImportantDatesPage() {
  const navigate = useNavigate();
  const { getAuthHeader, hasAccess } = useAuth();
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newDate, setNewDate] = useState({ name: '', date: '', type: 'birthday', recurring: true });

  useEffect(() => {
    if (hasAccess) {
      loadDates();
    }
  }, [hasAccess]);

  const loadDates = async () => {
    try {
      const res = await axios.get(`${API}/api/dates`, {
        headers: getAuthHeader()
      });
      setDates(res.data || []);
    } catch (err) {
      console.error('Failed to load dates:', err);
      toast.error('Kunde inte ladda datum');
    } finally {
      setLoading(false);
    }
  };

  const addDate = async () => {
    if (!newDate.name.trim() || !newDate.date) {
      toast.error('Fyll i namn och datum');
      return;
    }
    
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/dates`, {
        name: newDate.name,
        date: newDate.date,
        type: newDate.type,
        recurring: newDate.recurring
      }, {
        headers: getAuthHeader()
      });
      
      setDates([...dates, res.data]);
      setNewDate({ name: '', date: '', type: 'birthday', recurring: true });
      setShowAdd(false);
      toast.success('Datum tillagt');
    } catch (err) {
      console.error('Failed to add date:', err);
      toast.error('Kunde inte lägga till datum');
    } finally {
      setSaving(false);
    }
  };

  const deleteDate = async (id) => {
    try {
      await axios.delete(`${API}/api/dates/${id}`, {
        headers: getAuthHeader()
      });
      setDates(dates.filter(d => d.id !== id));
      toast.success('Datum borttaget');
    } catch (err) {
      console.error('Failed to delete date:', err);
      toast.error('Kunde inte ta bort datum');
    }
  };

  // Calculate days until date
  const daysUntil = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const targetDate = new Date(dateStr);
    
    // For recurring dates, find next occurrence
    targetDate.setFullYear(today.getFullYear());
    if (targetDate < today) {
      targetDate.setFullYear(today.getFullYear() + 1);
    }
    
    const diff = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Sort by days until
  const sortedDates = [...dates].sort((a, b) => daysUntil(a.date) - daysUntil(b.date));

  // Upcoming (within 30 days)
  const upcomingDates = sortedDates.filter(d => daysUntil(d.date) <= 30);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-white" data-testid="dates-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/category/planning')} className="rounded-full w-9 h-9 p-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">Viktiga datum</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att spara viktiga datum.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="dates-page">
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/category/planning')} className="rounded-full w-9 h-9 p-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-semibold">Viktiga datum</h1>
            <p className="text-xs text-muted-foreground">{dates.length} sparade datum</p>
          </div>
          <Button onClick={() => setShowAdd(true)} className="rounded-full" size="sm" data-testid="add-date-btn">
            <Plus className="w-4 h-4 mr-1" /> Lägg till
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Upcoming alert */}
        {upcomingDates.length > 0 && (
          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
            <h3 className="font-semibold text-amber-800 mb-2">📅 Kommande (30 dagar)</h3>
            <div className="space-y-2">
              {upcomingDates.slice(0, 3).map(d => {
                const days = daysUntil(d.date);
                const typeInfo = DATE_TYPES.find(t => t.id === d.type);
                return (
                  <div key={d.id} className="flex items-center gap-2 text-amber-700">
                    {typeInfo && <typeInfo.icon className="w-4 h-4" />}
                    <span className="font-medium">{d.name}</span>
                    <span className="text-amber-600 text-sm ml-auto">
                      {days === 0 ? 'Idag!' : days === 1 ? 'Imorgon' : `om ${days} dagar`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* All dates */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-500 rounded-full animate-spin" />
          </div>
        ) : sortedDates.length > 0 ? (
          <div className="space-y-2">
            {sortedDates.map(d => {
              const days = daysUntil(d.date);
              const typeInfo = DATE_TYPES.find(t => t.id === d.type);
              const dateObj = new Date(d.date);
              
              return (
                <div 
                  key={d.id}
                  className={`bg-white rounded-xl p-4 border border-border/30 flex items-center gap-3 ${days <= 7 ? 'border-amber-200 bg-amber-50/30' : ''}`}
                  data-testid={`date-item-${d.id}`}
                >
                  <div className={`w-10 h-10 rounded-xl ${typeInfo?.color || 'bg-slate-100'} flex items-center justify-center`}>
                    {typeInfo && <typeInfo.icon className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{d.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {dateObj.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' })}
                      {d.type === 'birthday' && ` • ${typeInfo?.label}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${days <= 7 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                      {days === 0 ? '🎉 Idag!' : days === 1 ? 'Imorgon' : `${days} dagar`}
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => deleteDate(d.id)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`delete-date-${d.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Inga datum sparade ännu</p>
          </div>
        )}
      </main>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4 animate-slide-up">
            <h3 className="font-heading text-lg font-semibold">Lägg till datum</h3>
            
            <Input
              placeholder="Namn (t.ex. Mammas födelsedag)"
              value={newDate.name}
              onChange={(e) => setNewDate({...newDate, name: e.target.value})}
              className="rounded-xl"
              data-testid="date-name-input"
            />
            
            <Input
              type="date"
              value={newDate.date}
              onChange={(e) => setNewDate({...newDate, date: e.target.value})}
              className="rounded-xl"
              data-testid="date-date-input"
            />
            
            <div>
              <label className="text-sm font-medium mb-2 block">Typ</label>
              <div className="flex flex-wrap gap-2">
                {DATE_TYPES.map(type => (
                  <Button
                    key={type.id}
                    variant={newDate.type === type.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewDate({...newDate, type: type.id})}
                    className="rounded-full"
                    data-testid={`date-type-${type.id}`}
                  >
                    <type.icon className="w-4 h-4 mr-1" />
                    {type.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAdd(false)} className="flex-1 rounded-full">
                Avbryt
              </Button>
              <Button onClick={addDate} className="flex-1 rounded-full" disabled={saving} data-testid="save-date-btn">
                {saving ? 'Sparar...' : 'Lägg till'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
